import { NUMBER } from 'common/constants/gridType';
import { largeCurrencyFormat } from 'common/formats/formats';
import { ParserParams } from 'common/types/scalarSpreadsheet';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';

export interface CommonInvestmentParserOptions {
  // Whether to use row.value directly for middle rows (true for fund, false for firm)
  useValueDirectly?: boolean;
  // Format to use for share count cells (largeDecimalFormat for fund, undefined for firm)
  shareCountFormat?: any;
  // Format to use for the first column in the last row (largeDecimalFormat for fund, undefined for firm)
  lastRowFirstColumnFormat?: any;
  useTotalValueCustomKey?: boolean;
}

const getExpressionForTotalValue = (
  rowExpr: string | undefined,
  columnLegend: string,
  useTotalValueCustomKey: boolean,
  columnIndex: number
) => {
  if (columnIndex === 0) {
    return 'Total:';
  }
  if (useTotalValueCustomKey) {
    return rowExpr;
  }
  return rowExpr?.replace(/@/g, columnLegend);
};

const commonInvestmentParser = <T>({
  rowConfig,
  columns,
  options = {},
}: ParserParams<T> & { options?: CommonInvestmentParserOptions }) => {
  const {
    useValueDirectly = false,
    shareCountFormat = undefined,
    lastRowFirstColumnFormat = undefined,
    useTotalValueCustomKey = false,
  } = options;

  const cells: Record<string, ICell> = {};
  const alphabet = ['A', 'B'];

  rowConfig.forEach((row, index) => {
    const rowExpr = row.expr as string | undefined;
    // Header row
    if (index === 0) {
      const key = 'A1';
      cells[key] = {
        ...row,
        key,
        columnLegend: 'A',
      };
    } else if (index === 1) {
      // Column labels row
      columns.forEach((column, columnIndex) => {
        const columnLegend = alphabet[columnIndex];
        const key = `${columnLegend}${index + 1}`;
        cells[key] = {
          ...row,
          key,
          columnLegend,
          value: column.label,
          readOnly: true,
        };
      });
    } else if (index === rowConfig.length - 1) {
      // Last row (totals)
      columns.forEach((_, columnIndex) => {
        const columnLegend = alphabet[columnIndex];
        const key = `${columnLegend}${index + 1}`;
        const expr = getExpressionForTotalValue(rowExpr, columnLegend, useTotalValueCustomKey, columnIndex);
        const format = columnIndex === 1 ? largeCurrencyFormat : lastRowFirstColumnFormat;
        cells[key] = {
          ...row,
          expr,
          value: expr,
          key,
          format,
          className: row.className,
          columnLegend,
          gridType: NUMBER,
          customKey: columnIndex === 1 ? 'totalValue' : undefined,
        };
      });
    } else {
      // Middle rows (data)
      const shareCountColumnLegend = alphabet[0];
      const valueColumnLegend = alphabet[1];
      const shareCountKey = `${shareCountColumnLegend}${index + 1}`;
      const valueKey = `${valueColumnLegend}${index + 1}`;

      // Share count cell
      cells[shareCountKey] = {
        value: useValueDirectly ? row.value : rowExpr?.replace(/@/g, shareCountColumnLegend),
        expr: useValueDirectly ? row.value : rowExpr?.replace(/@/g, shareCountColumnLegend),
        key: shareCountKey,
        columnLegend: shareCountColumnLegend,
        className: row.className,
        gridType: NUMBER,
        format: shareCountFormat,
      };

      // Value cell
      cells[valueKey] = {
        expr: useValueDirectly ? row.expr : rowExpr?.replace(/@/g, valueColumnLegend),
        value: useValueDirectly ? row.expr : rowExpr?.replace(/@/g, valueColumnLegend),
        gridType: NUMBER,
        format: largeCurrencyFormat,
        className: row.className,
        key: valueKey,
        columnLegend: valueColumnLegend,
      };
    }
  });

  return cells;
};

export default commonInvestmentParser;
